import { useCallback, useState } from "react";
import type { BrandMediaAspectRatio, BrandMediaItem } from "@/types/brandMedia.type";
import {
  MediaPickerBody,
  mediaPickerDescription,
  type MediaCreateTarget,
  type MediaPickOrigin,
  type MediaPickerTab,
} from "./MediaPickerBody";
import { KitModal } from "../kit";

type Tab = MediaPickerTab;

export interface MediaPickerModalProps {
  brandId: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * "select" hands one item back and closes (a slide slot asked for it).
   * "manage" is the library itself: preview and delete, nothing to hand back.
   */
  mode: "select" | "manage";
  /** Seeds the generate form so slide art comes out shaped like its slot. */
  defaultAspect?: BrandMediaAspectRatio;
  /**
   * Seeds the generate form's prompt (a strategy slot arrives with one written
   * for it), so the user can generate instantly or edit first.
   */
  initialPrompt?: string;
  /** Opens Generate by default. Pass "upload" or "library" to land on a backup. */
  initialTab?: Tab;
  /** Opens straight onto one item's detail, for a caller that already listed them. */
  initialPreviewId?: string;
  onSelect?: (item: BrandMediaItem, origin: MediaPickOrigin) => void;
  /** Where Upload and Generate create the image; the brand library when omitted. */
  createTarget?: MediaCreateTarget;
  canWrite?: boolean;
  title?: string;
  /** A Guest (a brand's client): no brand-library reads, only upload/generate. */
  hideLibrary?: boolean;
}

/**
 * The brand-media library as a modal of its own, opened by the Workshop panel
 * and the media view. `MediaPickerBody` is the reusable part: put it behind a
 * tab set when a surface needs references or trends alongside brand media (the
 * calendar's pictures use IdeaMediaDialog instead).
 */
export function MediaPickerModal({
  brandId,
  isOpen,
  onClose,
  mode,
  defaultAspect = "1:1",
  initialPrompt,
  initialTab = "generate",
  initialPreviewId,
  onSelect,
  canWrite = true,
  title,
  createTarget,
  hideLibrary = false,
}: MediaPickerModalProps) {
  const [tab, setTab] = useState<Tab>(canWrite ? initialTab : "library");

  const handleClose = useCallback(() => {
    setTab(canWrite ? initialTab : "library");
    onClose();
  }, [onClose, initialTab, canWrite]);

  const activeTab: Tab = canWrite ? tab : "library";

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={title ?? (mode === "select" ? "Add an image" : "Brand media")}
      description={mediaPickerDescription(activeTab, createTarget)}
      modalClassName="flex max-h-[85dvh] w-full max-w-[760px] flex-col p-4 sm:p-6"
    >
      <MediaPickerBody
        brandId={brandId}
        isOpen={isOpen}
        tab={tab}
        onTabChange={setTab}
        mode={mode}
        defaultAspect={defaultAspect}
        initialPrompt={initialPrompt}
        initialPreviewId={initialPreviewId}
        onSelect={onSelect}
        onClose={handleClose}
        canWrite={canWrite}
        createTarget={createTarget}
        hideLibrary={hideLibrary}
      />
    </KitModal>
  );
}
